import { Directive, ElementRef, Injector, Input, NgZone, OnDestroy, OnInit } from '@angular/core';
import * as textMask from 'vanilla-text-mask/dist/vanillaTextMask.js';
import { fromEvent } from 'rxjs';
import { NgControl } from '@angular/forms';
import { distinctUntilChanged } from 'rxjs/operators';

@Directive({
    selector: '[appMaskPinCode]',
    standalone: false
})
export class MaskPinCodeDirective implements OnDestroy, OnInit {

    maskOptions = {
        guide: false,
        showMask: false,
        mask: [],
        keepCharPositions: false
    };

    maskedInputController;

    ngControl: NgControl;

    ngZone: NgZone;

    @Input()
    set pinCodeMask(mask: string) {
        if (mask) {
            this.maskOptions.mask = this.parseMask(mask);
            if (this.element?.nativeElement) {
                const inputCharCount = this.getInputCharacterCount(mask);
                this.element.nativeElement.setAttribute('maxlength', inputCharCount.toString());
            }
        }
    }

    private getInputCharacterCount(mask: string): number {
        return (mask.match(/[#X]/g) || []).length;
    }

    private getInputCharacterCountFromMask(maskArray: (string | RegExp)[]): number {
        return maskArray.filter(item => item instanceof RegExp).length;
    }

    constructor(private element: ElementRef,
                private injector: Injector) {
    }

    ngOnInit(): void {
        this.ngControl = this.injector.get(NgControl);
        this.ngZone = this.injector.get(NgZone);

        this.maskedInputController = textMask.maskInput({
            inputElement: this.element.nativeElement,
            ...this.maskOptions,
        });

        fromEvent(this.element.nativeElement, 'beforeinput')
            .subscribe((event: InputEvent) => {
                if (this.maskOptions.mask && this.maskOptions.mask.length > 0) {
                    const input = event.target as HTMLInputElement;
                    const currentValue = input.value || '';
                    const cleanedCurrent = this.cleanMaskedValue(currentValue);
                    const maxLength = this.getInputCharacterCountFromMask(this.maskOptions.mask);
                    const selectionStart = input.selectionStart || 0;
                    const selectionEnd = input.selectionEnd || 0;
                    
                    if (event.data && cleanedCurrent.length >= maxLength) {
                        if (selectionStart === selectionEnd) {
                            event.preventDefault();
                            return;
                        }
                    }
                    
                    const textBefore = currentValue.substring(0, selectionStart);
                    const textAfter = currentValue.substring(selectionEnd);
                    const newValue = textBefore + (event.data || '') + textAfter;
                    const cleanedNew = this.cleanMaskedValue(newValue);
                    
                    if (cleanedNew.length > maxLength) {
                        event.preventDefault();
                    }
                }
            });

        fromEvent(this.element.nativeElement, 'keyup')
            .pipe(distinctUntilChanged())
            .subscribe((event: InputEvent) => {
                this.ngZone.run(() => {
                    const input = event.target as HTMLInputElement;
                    const {value} = input;
                    if (this.ngControl?.control) {
                        const cleanedValue = this.cleanMaskedValue(value);
                        this.ngControl.viewToModelUpdate(cleanedValue);
                        if (input.value !== cleanedValue) {
                            input.value = cleanedValue;
                        }
                        this.ngControl.control.markAsDirty();
                    }
                });
            });

        fromEvent(this.element.nativeElement, 'input')
            .pipe(distinctUntilChanged())
            .subscribe((event: InputEvent) => {
                this.ngZone.run(() => {
                    const input = event.target as HTMLInputElement;
                    const {value} = input;
                    if (this.ngControl?.control) {
                        const cleanedValue = this.cleanMaskedValue(value);
                        this.ngControl.viewToModelUpdate(cleanedValue);
                        if (input.value !== cleanedValue) {
                            input.value = cleanedValue;
                        }
                    }
                });
            });
    }

    ngOnDestroy(): void {
        this.maskedInputController.destroy();
    }

    private parseMask(mask: string): (string | RegExp)[] {
        const maskArray: (string | RegExp)[] = [];
        
        for (const char of mask) {
            switch (char) {
                case 'X':
                    maskArray.push(/[A-Za-z]/);
                    break;
                case '#':
                    maskArray.push(/\d/);
                    break;
                default:
                    maskArray.push(char);
                    break;
            }
        }
        
        return maskArray;
    }

    private cleanMaskedValue(value: string): string {
        if (!value) {
            return '';
        }
        
        let cleaned = value;
        
        while (cleaned.endsWith('_')) {
            cleaned = cleaned.slice(0, -1);
        }
        
        cleaned = cleaned.replace(/[^A-Za-z0-9\-_\.]+$/, '');
        
        if (this.maskOptions.mask && this.maskOptions.mask.length > 0) {
            const expectedLength = this.getInputCharacterCountFromMask(this.maskOptions.mask);
            if (cleaned.length > expectedLength) {
                cleaned = cleaned.substring(0, expectedLength);
            }
        }
        
        return cleaned;
    }
} 
